import { Badge } from "@designs-by-maryam/ui/components/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@designs-by-maryam/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FolderOpen, Image, Palette } from "lucide-react";
import { requireDesigner } from "@/functions/require-role";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/designer/")({
	component: DesignerDashboard,
	beforeLoad: async () => {
		// Server-side role check - no client-side flash
		await requireDesigner();
	},
});

function DesignerDashboard() {
	const designAssets = useQuery(orpc.getDesignAssets.queryOptions());

	return (
		<div className="container mx-auto p-6">
			<div className="mb-8">
				<h1 className="flex items-center gap-3 font-bold text-3xl">
					<Palette className="h-8 w-8 text-primary" />
					Designer Dashboard
				</h1>
				<p className="mt-2 text-muted-foreground">
					Manage your design assets and client projects
				</p>
			</div>

			<div className="grid gap-6 md:grid-cols-3">
				{/* Projects Card */}
				<div className="rounded-lg border bg-card p-6">
					<div className="mb-4 flex items-center gap-3">
						<div className="rounded-full bg-primary/10 p-3">
							<FolderOpen className="h-6 w-6 text-primary" />
						</div>
						<h2 className="font-semibold text-xl">Projects</h2>
					</div>
					<p className="font-bold text-3xl">0</p>
					<p className="mt-2 text-muted-foreground text-sm">Active projects</p>
				</div>

				{/* Assets Card */}
				<div className="rounded-lg border bg-card p-6">
					<div className="mb-4 flex items-center gap-3">
						<div className="rounded-full bg-primary/10 p-3">
							<Image className="h-6 w-6 text-primary" />
						</div>
						<h2 className="font-semibold text-xl">Assets</h2>
					</div>
					<p className="font-bold text-3xl">
						{designAssets.data?.assets?.length ?? 0}
					</p>
					<p className="mt-2 text-muted-foreground text-sm">Design assets</p>
				</div>

				{/* Designer Info Card */}
				<div className="rounded-lg border bg-card p-6">
					<div className="mb-4 flex items-center gap-3">
						<div className="rounded-full bg-primary/10 p-3">
							<Palette className="h-6 w-6 text-primary" />
						</div>
						<h2 className="font-semibold text-xl">Your Role</h2>
					</div>
					{designAssets.data?.user && (
						<div className="space-y-1">
							<p className="font-medium text-sm">Designer Access</p>
							<p className="text-muted-foreground text-xs">
								Role: {designAssets.data.user.role}
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Design Assets Section */}
			<div className="mt-6 rounded-lg border bg-card p-6">
				<h3 className="mb-4 font-semibold text-lg">Design Assets</h3>

				{designAssets.isLoading && (
					<p className="text-muted-foreground">Loading assets...</p>
				)}

				{designAssets.error && (
					<div className="rounded-lg border border-destructive bg-destructive/10 p-4">
						<p className="font-medium text-destructive">Error loading assets</p>
						<p className="mt-1 text-muted-foreground text-sm">
							{designAssets.error.message}
						</p>
					</div>
				)}

				{designAssets.data &&
					(designAssets.data.assets && designAssets.data.assets.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Last Modified</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{designAssets.data.assets.map(
									(asset: {
										id: string;
										name: string;
										type?: string;
										status?: string;
										updatedAt?: string;
									}) => (
										<TableRow key={asset.id}>
											<TableCell className="font-medium">
												{asset.name}
											</TableCell>
											<TableCell>
												<Badge variant="outline">
													{asset.type || "Unknown"}
												</Badge>
											</TableCell>
											<TableCell>
												<Badge
													variant={
														asset.status === "published"
															? "default"
															: "secondary"
													}
												>
													{asset.status || "Draft"}
												</Badge>
											</TableCell>
											<TableCell className="text-muted-foreground">
												{asset.updatedAt
													? new Date(asset.updatedAt).toLocaleDateString()
													: "—"}
											</TableCell>
										</TableRow>
									),
								)}
							</TableBody>
						</Table>
					) : (
						<p className="py-8 text-center text-muted-foreground">
							No design assets yet. Start by creating a new project.
						</p>
					))}
			</div>
		</div>
	);
}
